
import { gtag, isGtagAvailable } from './gtag';
import { LeadFormData } from '@/types/lead';
import { GOOGLE_ADS_CONFIG } from './config';
import { resolveTrafficSource } from '@/utils/trafficSource';

export interface ConversionData {
  send_to: string;
  value?: number;
  currency?: string;
  transaction_id?: string;
  custom_parameters?: Record<string, any>;
}

// Module-level dedup: prevents the same transaction_id from being sent more than once
// within a page session (protects against double-clicks, StrictMode double-invokes, retries).
const sentTransactions = new Set<string>();

const sendConversionOnce = (data: ConversionData): boolean => {
  const txId = data.transaction_id;
  if (txId && sentTransactions.has(txId)) {
    console.warn('[GAds] duplicate conversion suppressed:', data.send_to, 'tx=', txId);
    return false;
  }
  if (txId) sentTransactions.add(txId);
  gtag('event', 'conversion', data);
  if (import.meta.env.DEV) {
    console.log('[GAds] conversion sent:', data.send_to, 'tx=', txId);
  }
  return true;
};


// Track form submission (when user completes the lead form)
export const trackFormSubmission = (
  leadData: LeadFormData,
  utmParams?: Record<string, string>
) => {
  if (!isGtagAvailable()) {
    console.warn('gtag not available for form submission tracking');
    return;
  }

  console.log('Tracking form submission:', leadData.name);

  const conversionData: ConversionData = {
    send_to: `${GOOGLE_ADS_CONFIG.CONVERSION_ID}/${GOOGLE_ADS_CONFIG.CONVERSIONS.FORM_SUBMITTED}`,
    value: GOOGLE_ADS_CONFIG.VALUES.FORM_SUBMITTED,
    currency: 'BRL',
    transaction_id: `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    custom_parameters: {
      lead_name: leadData.name,
      lead_whatsapp: leadData.whatsapp,
      lead_cnpj: leadData.cnpj,
      ...utmParams
    }
  };

  // Track conversion
  gtag('event', 'conversion', conversionData);

  // Enhanced conversion with user data
  gtag('event', 'conversion', {
    ...conversionData,
    user_data: {
      phone_number: leadData.whatsapp
    }
  });

  // Track as GA4 custom event for analytics reporting
  const { sourceName } = resolveTrafficSource();
  gtag('event', 'generate_lead', {
    event_category: 'form',
    event_label: 'calculator_form',
    lead_source: 'website',
    traffic_source: sourceName,
    ...utmParams
  });

  console.log('Form submission tracked:', conversionData);
};

// Track WhatsApp button clicks (any CTA that leads to WhatsApp)
// Returns a Promise that resolves when the Google Ads beacon confirms (event_callback)
// or after an 800ms safety timeout — whichever comes first. This lets callers await
// the beacon BEFORE redirecting via window.open, which is critical for Smart Bidding
// attribution and Enhanced Conversions match quality.
export const trackWhatsAppClick = (
  source: string,
  utmParams?: Record<string, string>,
  dedupKey?: string,
  leadData?: { name?: string; whatsapp?: string }
): Promise<void> => {
  // If gtag isn't available (preview/dev/opt-out), resolve immediately — never block redirect.
  if (!isGtagAvailable()) {
    if (import.meta.env.DEV) console.warn('gtag not available for WhatsApp click tracking');
    return Promise.resolve();
  }

  const txId = dedupKey ?? `whatsapp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

  // Hard guard: same dedupKey never fires twice in one page session.
  if (sentTransactions.has(txId)) {
    console.warn('[GAds] duplicate WhatsApp conversion suppressed:', txId);
    return Promise.resolve();
  }
  sentTransactions.add(txId);

  console.log('Tracking WhatsApp click from:', source);

  // Build Enhanced Conversions user_data (plaintext; gtag hashes with SHA-256 client-side
  // because AW-... is configured with allow_enhanced_conversions: true in index.html).
  let userData: Record<string, any> | undefined;
  if (leadData?.whatsapp || leadData?.name) {
    userData = {};
    if (leadData.whatsapp) {
      const digits = leadData.whatsapp.replace(/\D/g, '');
      // E.164 with Brazil country code
      userData.phone_number = digits.startsWith('55') ? `+${digits}` : `+55${digits}`;
    }
    if (leadData.name) {
      const parts = leadData.name.trim().split(/\s+/);
      userData.address = {
        first_name: parts[0] ?? '',
        last_name: parts.slice(1).join(' '),
      };
    }
  }

  // GA4 custom event (fire-and-forget; not a conversion, no dedup needed)
  gtag('event', 'whatsapp_click', {
    event_category: 'engagement',
    event_label: source,
    button_location: source,
    ...utmParams,
  });

  // Google Ads conversion with event_callback — resolves the Promise when the beacon
  // is acknowledged. 800ms safety timeout so we never hang the redirect.
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    const payload: Record<string, any> = {
      send_to: `${GOOGLE_ADS_CONFIG.CONVERSION_ID}/${GOOGLE_ADS_CONFIG.CONVERSIONS.WHATSAPP_CLICK}`,
      value: GOOGLE_ADS_CONFIG.VALUES.WHATSAPP_CLICK,
      currency: 'BRL',
      transaction_id: txId,
      event_callback: finish,
      custom_parameters: {
        click_source: source,
        ...utmParams,
      },
    };
    if (userData) payload.user_data = userData;

    gtag('event', 'conversion', payload);
    if (import.meta.env.DEV) {
      console.log('[GAds] conversion sent:', payload.send_to, 'tx=', txId, 'ec=', !!userData);
    }

    // Safety net — never block the user longer than 800ms
    setTimeout(finish, 800);
  });
};

